import { Link, useParams, useNavigate, useLocation } from 'react-router-dom'
import { Loader2, CheckCircle, Plus, Home } from 'lucide-react'
import { BuildingForm } from '@/components/buildings/BuildingForm'
import { BuildingPropertiesTable } from '@/components/buildings/BuildingPropertiesTable'
import {
  useBuilding,
  useUpdateBuilding,
  useDeleteBuilding,
  usePropertiesByBuilding,
  type BuildingUpdateInput,
  type BuildingId,
} from '@/hooks'
import { useEffect, useState } from 'react'

/**
 * Pagina de edicion de edificios - carga datos de edificio existente para editar.
 * Usa React Hook Form + Zod para validacion y Convex para obtencion de datos/mutaciones.
 */
export function BuildingEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // Show success message from navigation state (e.g., after creation)
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  )

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  // Clear location state after reading to prevent message on refresh
  useEffect(() => {
    if (location.state) {
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, location.state, navigate])

  // Fetch building data
  const { data: building, isLoading, error } = useBuilding(id)
  const updateMutation = useUpdateBuilding()
  const deleteMutation = useDeleteBuilding()

  // Fetch properties count for the section header
  const { data: properties } = usePropertiesByBuilding({
    buildingId: id as BuildingId,
  })
  const propertiesCount = properties?.length ?? 0
  const publishedCount = properties?.filter((property) => property.status === 'published').length ?? 0

  async function handleSubmit(formData: BuildingUpdateInput) {
    if (!id) return

    try {
      await updateMutation.mutateAsync({ id, data: formData })
      // Navigate to buildings list after successful update
      navigate('/buildings', {
        state: { message: '¡Edificio actualizado exitosamente!' },
      })
    } catch (err) {
      // Error is already captured in updateMutation.error
      console.error('Failed to update building:', err)
    }
  }

  async function handleDelete() {
    if (!id) return

    try {
      await deleteMutation.mutateAsync(id)
      navigate('/buildings', {
        state: { message: 'Edificio eliminado exitosamente.' },
      })
    } catch (err) {
      // Error is already captured in deleteMutation.error
      console.error('Failed to delete building:', err)
    }
  }

  // Estado de carga
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Cargando edificio...</span>
        </div>
      </div>
    )
  }

  // Estado de error
  if (error) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <Link
            to="/buildings"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            ← Volver a Edificios
          </Link>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center dark:border-red-800 dark:bg-red-900/20">
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400">
            Error al cargar el edificio
          </h2>
          <p className="mt-2 text-sm text-red-600 dark:text-red-300">
            {error.message}
          </p>
          <Link
            to="/buildings"
            className="mt-4 inline-block rounded-md bg-red-100 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
          >
            Volver a Edificios
          </Link>
        </div>
      </div>
    )
  }

  // Estado de no encontrado
  if (!building) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <Link
            to="/buildings"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            ← Volver a Edificios
          </Link>
        </div>
        <div className="rounded-lg border bg-muted/50 p-6 text-center">
          <h2 className="text-lg font-semibold">Edificio no encontrado</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            El edificio que buscas no existe o ha sido eliminado.
          </p>
          <Link
            to="/buildings"
            className="mt-4 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Volver a Edificios
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
          <CheckCircle className="h-4 w-4" />
          {successMessage}
        </div>
      )}

      {/* Encabezado */}
      <div>
        <Link
          to="/buildings"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          ← Volver a Edificios
        </Link>
        <h1 className="mt-4 text-3xl font-bold tracking-tight">Editar Edificio</h1>
        <p className="mt-2 text-muted-foreground">
          Editando: <span className="font-medium text-foreground">{building.name}</span>
        </p>
      </div>

      {/* Properties / Viviendas Section */}
      <section className="space-y-4 pt-6 border-t">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Home className="h-5 w-5 text-muted-foreground" />
            <div>
              <h2 className="text-xl font-semibold tracking-tight">
                Propiedades / Viviendas
              </h2>
              <p className="text-sm text-muted-foreground">
                {propertiesCount === 0
                  ? 'Sin propiedades asignadas'
                  : propertiesCount === 1
                    ? '1 propiedad asignada'
                    : `${propertiesCount} propiedades asignadas`}
              </p>
            </div>
          </div>
          <Link to={`/properties?buildingId=${id}`} className="rounded-md border px-3 py-2 text-sm hover:bg-muted">Ver / buscar viviendas →</Link>
          <Link
            to={`/properties/create?building=${id}`}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Añadir Propiedad
          </Link>
        </div>

        <p className="text-sm text-muted-foreground">{publishedCount} viviendas publicadas. Este estado describe la web; no acredita disponibilidad comercial ni una reserva del edificio.</p>

        {/* Properties Table */}
        <BuildingPropertiesTable
          buildingId={id as BuildingId}
          showAdminActions={true}
          showReserveButton={false}
        />
      </section>

      {/* Form */}
      <BuildingForm
        building={building}
        isEdit
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        isSubmitting={updateMutation.isPending}
        isDeleting={deleteMutation.isPending}
        submitError={updateMutation.error?.message ?? deleteMutation.error?.message}
      />
    </div>
  )
}
